/**
 * ResizeService.ts
 * 
 * 注意：此服务已被部分启用，元素大小调整功能已在CanvasBoard中实现。
 * 对于图片元素，已实现等比例缩放功能，保持宽高比例。
 * 
 * 保留此服务主要用于控制点检测和状态管理，具体的大小调整逻辑在CanvasBoard组件中。
 */

import { Bounds, Point, TextData, CardData, ImageData } from '../../types';
import { ViewportService } from './ViewportService';

// 保留空的控制点枚举，避免引用错误
export enum ControlPoint {
  TOP_LEFT,
  TOP_RIGHT,
  BOTTOM_LEFT,
  BOTTOM_RIGHT,
  TOP_CENTER,
  BOTTOM_CENTER,
  LEFT_CENTER,
  RIGHT_CENTER
}

export class ResizeService {
  private ctx: CanvasRenderingContext2D | null = null;
  private viewportService: ViewportService | null = null;
  private isResizing: boolean = false;
  private activeControlPoint: ControlPoint | null = null;
  private resizeStartPoint: Point | null = null;
  private originalBounds: Bounds | null = null;
  private resizingElementType: 'text' | 'card' | 'image' | null = null;
  private resizingElementIndex: number = -1;
  
  constructor(viewportService?: ViewportService) {
    this.viewportService = viewportService || null;
  }
  
  // 设置画布上下文
  public setContext(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
  }
  
  // 空方法 - 返回null表示没有控制点
  public isPointInControlPoint(point: Point, bounds: Bounds): ControlPoint | null {
    if (!this.ctx) return null;
    
    const controlPoints = this.getControlPoints(bounds);
    for (const [controlPoint, position] of Array.from(controlPoints.entries())) {
      if (this.isPointInCircle(point, position, 4)) {
        return controlPoint;
      }
    }
    return null;
  }
  
  // 空方法 - 不执行任何操作
  public startResize(point: Point, controlPoint: ControlPoint, bounds: Bounds, elementType: 'text' | 'card' | 'image', elementIndex: number): void {
    this.isResizing = true;
    this.activeControlPoint = controlPoint;
    this.resizeStartPoint = point;
    this.originalBounds = { ...bounds };
    this.resizingElementType = elementType;
    this.resizingElementIndex = elementIndex;
  }
  
  // 空方法 - 返回null表示没有调整
  public resizeElement(point: Point, texts: TextData[], cards: CardData[], images: ImageData[]): Bounds | null {
    if (!this.isResizing || !this.resizeStartPoint || !this.originalBounds || !this.activeControlPoint) {
      return null;
    }
    
    const dx = point.x - this.resizeStartPoint.x;
    const dy = point.y - this.resizeStartPoint.y;
    
    const newBounds = { ...this.originalBounds };
    const controlPoint = this.activeControlPoint as ControlPoint;
    
    switch (controlPoint) {
      case ControlPoint.TOP_LEFT:
        newBounds.minX += dx;
        newBounds.minY += dy;
        newBounds.maxX -= dx;
        newBounds.maxY -= dy;
        break;
      case ControlPoint.TOP_RIGHT:
        newBounds.maxX += dx;
        newBounds.minY += dy;
        newBounds.minX -= dx;
        newBounds.maxY -= dy;
        break;
      case ControlPoint.BOTTOM_LEFT:
        newBounds.minX += dx;
        newBounds.maxY += dy;
        newBounds.maxX -= dx;
        newBounds.minY -= dy;
        break;
      case ControlPoint.BOTTOM_RIGHT:
        newBounds.maxX += dx;
        newBounds.maxY += dy;
        newBounds.minX -= dx;
        newBounds.minY -= dy;
        break;
      case ControlPoint.TOP_CENTER:
        newBounds.minY += dy;
        newBounds.maxY -= dy;
        break;
      case ControlPoint.BOTTOM_CENTER:
        newBounds.maxY += dy;
        newBounds.minY -= dy;
        break;
      case ControlPoint.LEFT_CENTER:
        newBounds.minX += dx;
        newBounds.maxX -= dx;
        break;
      case ControlPoint.RIGHT_CENTER:
        newBounds.maxX += dx;
        newBounds.minX -= dx;
        break;
    }
    
    return newBounds;
  }
  
  // 空方法 - 不执行任何操作
  public stopResize(): void {
    this.isResizing = false;
    this.activeControlPoint = null;
    this.resizeStartPoint = null;
    this.originalBounds = null;
    this.resizingElementType = null;
    this.resizingElementIndex = -1;
  }
  
  // 返回false表示没有元素正在调整大小
  public isElementResizing(): boolean {
    return this.isResizing;
  }
  
  // 返回空信息
  public getResizingElementInfo(): { type: 'text' | 'card' | 'image' | null, index: number } {
    return { type: this.resizingElementType, index: this.resizingElementIndex };
  }
  
  // 空方法 - 不执行任何绘制
  public drawResizePreview(bounds: Bounds): void {
    if (!this.ctx) return;
    
    const controlPoints = this.getControlPoints(bounds);
    for (const [controlPoint, position] of Array.from(controlPoints.entries())) {
      const isActive = controlPoint === this.activeControlPoint;
      this.drawControlPoint(position, isActive);
    }
  }
  
  private getControlPoints(bounds: Bounds): Map<ControlPoint, Point> {
    const points = new Map<ControlPoint, Point>();
    points.set(ControlPoint.TOP_LEFT, { x: bounds.minX, y: bounds.minY });
    points.set(ControlPoint.TOP_RIGHT, { x: bounds.maxX, y: bounds.minY });
    points.set(ControlPoint.BOTTOM_LEFT, { x: bounds.minX, y: bounds.maxY });
    points.set(ControlPoint.BOTTOM_RIGHT, { x: bounds.maxX, y: bounds.maxY });
    points.set(ControlPoint.TOP_CENTER, { x: (bounds.minX + bounds.maxX) / 2, y: bounds.minY });
    points.set(ControlPoint.BOTTOM_CENTER, { x: (bounds.minX + bounds.maxX) / 2, y: bounds.maxY });
    points.set(ControlPoint.LEFT_CENTER, { x: bounds.minX, y: (bounds.minY + bounds.maxY) / 2 });
    points.set(ControlPoint.RIGHT_CENTER, { x: bounds.maxX, y: (bounds.minY + bounds.maxY) / 2 });
    return points;
  }
  
  private drawControlPoint(position: Point, isActive: boolean): void {
    if (!this.ctx) return;
    
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc(position.x, position.y, 4, 0, Math.PI * 2);
    this.ctx.strokeStyle = '#2196F3';
    this.ctx.lineWidth = 2;
    
    if (isActive) {
      this.ctx.fillStyle = '#2196F3';
      this.ctx.fill();
    }
    
    this.ctx.stroke();
    this.ctx.restore();
  }
  
  private isPointInCircle(point: Point, center: Point, radius: number): boolean {
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    return dx * dx + dy * dy <= radius * radius;
  }
}